/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Button, Card, SvgIcon, Table } from "core/components";
import formatter from "core/formatter";
import moment from "moment";
import { navigateTo } from "core/navigation";
import { print_material_direct_site_delivery_path, site_deliveries_path } from "routes";

type DeliveryProps = {
  delivery: Record<string, unknown>;
};

const Delivery = ({ delivery }: DeliveryProps): JSX.Element => {
  const table = React.useRef(null);
  const reorderingColumns = delivery.delivery_items[0]?.project_code ? [] : ["project_code", "actions"];
  const groupBy = delivery.delivery_items[0]?.project_code ? ["project_code"] : [];
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Inventory Item",
        accessor: "item",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "project_code",
        Header: "Project ID",
        accessor: "project_code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-bold text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Purchase Request",
        accessor: "purchase_request_code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Delivered Quantity",
        accessor: "delivered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "actions",
        Header: "Actions",
        accessor: "",
        Aggregated: ({ row }) => {
          const deliveryId = row.subRows[0].original.delivery_id;
          const project_code = row.subRows[0].original.project_code;
          const project_name = row.subRows[0].original.project_name;

          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-primary"
                title="Print Material Direct"
                data-bs-toggle="tooltip"
                onClick={() => {
                  window.open(print_material_direct_site_delivery_path(deliveryId, { project_code, project_name }));
                }}
              >
                <SvgIcon name="printer" className="h5" />
              </a>
            </div>
          );
        },
        Cell: () => {
          return <></>;
        }
      }
    ],
    []
  );

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table
            columns={columns}
            data={delivery.delivery_items}
            hiddenColumns={[...reorderingColumns]}
            groupBy={[...groupBy]}
            ref={table}
            skipPageReset={true}
          />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return (
      <>
        <Row className="mb-4">
          <Col>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Purchase Order:</h5>
              </Col>
              <Col>{delivery.purchase_order.code}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Warehouse:</h5>
              </Col>
              <Col>{delivery.warehouse.name}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Ordered By:</h5>
              </Col>
              <Col>{delivery.ordered_by}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Delivered By:</h5>
              </Col>
              <Col>{delivery.delivered_by}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Received By:</h5>
              </Col>
              <Col>{delivery.received_by}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Remarks:</h5>
              </Col>
              <Col>{delivery.remarks}</Col>
            </Row>
          </Col>
          <Col>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Delivery Date:</h5>
              </Col>
              <Col>{moment(delivery.created_at).format("LL")}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Partial Delivery:</h5>
              </Col>
              <Col>{delivery.purchase_order.allow_partial_delivery ? "Yes" : "No"}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Supplier:</h5>
              </Col>
              <Col>{delivery.supplier}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Total Items:</h5>
              </Col>
              <Col>{`${delivery.total_complete_items}/${delivery.total_items}`}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Subtotal Amount:</h5>
              </Col>
              <Col>{formatter.format(parseFloat(delivery.purchase_order.subtotal_amount))}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Value Added Tax:</h5>
              </Col>
              <Col>{formatter.format(parseFloat(delivery.purchase_order.value_added_tax_amount))}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Creditable Withholding Tax:</h5>
              </Col>
              <Col>{`(${formatter.format(parseFloat(delivery.purchase_order.withholding_tax_amount))})`}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Total Amount:</h5>
              </Col>
              <Col>{formatter.format(parseFloat(delivery.purchase_order.total_amount))}</Col>
            </Row>
          </Col>
        </Row>
        {dataTable()}
      </>
    );
  };

  return (
    <React.StrictMode>
      <Container fluid={true}>
        <Row>
          <Col>
            <Card>
              <Card.Header title={`Delivery`}>
                <Button
                  title="Back"
                  className="button--default"
                  icon="arrow-left"
                  onClick={() => {
                    navigateTo(site_deliveries_path());
                  }}
                />
              </Card.Header>
              <Card.Body>{content()}</Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.StrictMode>
  );
};

export default Delivery;
